import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { boolean, object, string } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm, useWatch } from 'react-hook-form';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import Page from '@/components/Page';
import HeaderBreadcrumbs from '@/components/HeaderBreadcrumbs';
import { FormProvider, RHFTextField } from '@/components/hook-form';
import { SkeletonLoadingTableData } from '@/components/skeleton';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';

import { ROOTS_SETTINGS, settingsPath } from '../routes/paths';
import {
  getEmailTemplateStyle,
  getEmailTemplate,
  previewEmailTemplate,
  putEmailTemplateStyle,
  putEmailTemplate,
} from '../api';
import {
  EmailTemplateFormValues,
  EmailTemplatePreviewPayload,
  EmailTemplateStyleFormValues,
} from '../types';
import {
  EMAIL_TEMPLATE_THEME_DEFAULTS,
  emailTemplateFormToPayload,
  emailTemplateStyleFormToPayload,
  emailTemplateStyleToFormValues,
  emailTemplateToFormValues,
  formatEmailTemplateVariables,
  getEmailTemplateStyleResetPayload,
  getEmailTemplateRevertPayload,
} from '../utils';

const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;

const EMAIL_TEMPLATE_STYLE_COLOR_FIELDS = [
  {
    name: 'page_background_color',
    summaryLabel: 'Page',
    editorLabel: 'Page background',
  },
  {
    name: 'card_background_color',
    summaryLabel: 'Card',
    editorLabel: 'Card background',
  },
  { name: 'border_color', summaryLabel: 'Border', editorLabel: 'Border' },
  { name: 'text_color', summaryLabel: 'Text', editorLabel: 'Text' },
  {
    name: 'muted_text_color',
    summaryLabel: 'Muted',
    editorLabel: 'Muted text',
  },
  {
    name: 'accent_color',
    summaryLabel: 'Accent',
    editorLabel: 'Accent',
  },
  {
    name: 'button_text_color',
    summaryLabel: 'Button text',
    editorLabel: 'Button text',
  },
] as const;

type EmailTemplateStyleColorName =
  (typeof EMAIL_TEMPLATE_STYLE_COLOR_FIELDS)[number]['name'];

const defaultValues: EmailTemplateFormValues = {
  subject: '',
  body_html: '',
  body_text: '',
};

const styleDefaultValues: EmailTemplateStyleFormValues = {
  ...EMAIL_TEMPLATE_THEME_DEFAULTS,
  logo_enabled: false,
  logo_url: '',
};

const EmailTemplateSchema = object().shape({
  subject: string().required('Subject is required'),
  body_html: string(),
  body_text: string(),
});

const EmailTemplateStyleSchema = object().shape({
  page_background_color: string()
    .matches(HEX_COLOR_REGEX, 'Use a 6-digit hex color')
    .required('Background color is required'),
  card_background_color: string()
    .matches(HEX_COLOR_REGEX, 'Use a 6-digit hex color')
    .required('Card color is required'),
  border_color: string()
    .matches(HEX_COLOR_REGEX, 'Use a 6-digit hex color')
    .required('Border color is required'),
  text_color: string()
    .matches(HEX_COLOR_REGEX, 'Use a 6-digit hex color')
    .required('Text color is required'),
  muted_text_color: string()
    .matches(HEX_COLOR_REGEX, 'Use a 6-digit hex color')
    .required('Muted text color is required'),
  accent_color: string()
    .matches(HEX_COLOR_REGEX, 'Use a 6-digit hex color')
    .required('Accent color is required'),
  button_text_color: string()
    .matches(HEX_COLOR_REGEX, 'Use a 6-digit hex color')
    .required('Button text color is required'),
  logo_enabled: boolean().required(),
  logo_url: string(),
});

type EmailTemplateEditorProps = {
  templateId: number;
  onSaved?: VoidFunction;
  previewStyle?: EmailTemplateStyleFormValues | null;
};

type EmailTemplateStyleEditorProps = {
  onStyleChange?: (values: EmailTemplateStyleFormValues | null) => void;
};

export function EmailTemplateStyleEditor({
  onStyleChange,
}: EmailTemplateStyleEditorProps) {
  const queryClient = useQueryClient();
  const { errorMsg, successMsg } = useSnackbarMsg();
  const [isStyleEditorOpen, setIsStyleEditorOpen] = useState(false);

  const methods = useForm<EmailTemplateStyleFormValues>({
    resolver: yupResolver(EmailTemplateStyleSchema),
    defaultValues: styleDefaultValues,
  });

  const { control, handleSubmit, reset } = methods;

  const { isLoading } = useQuery({
    queryKey: ['emailTemplateStyle'],
    queryFn: getEmailTemplateStyle,
    onSuccess: (data) => {
      reset(emailTemplateStyleToFormValues(data));
    },
    onError: (error) =>
      errorMsg(error, 'Something went wrong while fetching email style'),
  });

  const watchedStyle = useWatch({ control });
  const currentStyle = emailTemplateStyleToFormValues(watchedStyle);
  const {
    accent_color,
    border_color,
    button_text_color,
    card_background_color,
    logo_enabled,
    logo_url,
    muted_text_color,
    page_background_color,
    text_color,
  } = currentStyle;

  useEffect(() => {
    onStyleChange?.({
      accent_color,
      border_color,
      button_text_color,
      card_background_color,
      logo_enabled,
      logo_url,
      muted_text_color,
      page_background_color,
      text_color,
    });
  }, [
    accent_color,
    border_color,
    button_text_color,
    card_background_color,
    logo_enabled,
    logo_url,
    muted_text_color,
    page_background_color,
    text_color,
    onStyleChange,
  ]);

  const saveStyleMutation = useMutation({
    mutationFn: (values: EmailTemplateStyleFormValues) =>
      putEmailTemplateStyle(emailTemplateStyleFormToPayload(values)),
    onSuccess: (data) => {
      queryClient.setQueryData(['emailTemplateStyle'], data);
      reset(emailTemplateStyleToFormValues(data));
      successMsg('Email style updated successfully');
    },
    onError: (error) =>
      errorMsg(error, 'Something went wrong while updating email style'),
  });

  const resetStyleMutation = useMutation({
    mutationFn: () =>
      putEmailTemplateStyle(getEmailTemplateStyleResetPayload()),
    onSuccess: (data) => {
      queryClient.setQueryData(['emailTemplateStyle'], data);
      reset(emailTemplateStyleToFormValues(data));
      successMsg('Email style reset to defaults');
    },
    onError: (error) =>
      errorMsg(error, 'Something went wrong while resetting email style'),
  });

  const isStyleMutationLoading =
    saveStyleMutation.isLoading || resetStyleMutation.isLoading;
  const getStyleColor = (name: EmailTemplateStyleColorName) =>
    currentStyle[name] || EMAIL_TEMPLATE_THEME_DEFAULTS[name];

  if (isLoading) {
    return <SkeletonLoadingTableData />;
  }

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'flex-start' }}
          >
            <Box>
              <Typography variant="h6">Email Style</Typography>
              <Typography
                variant="body2"
                sx={{ mt: 0.5, color: 'text.secondary' }}
              >
                Set one shared style per business. Template editors only control
                content.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => setIsStyleEditorOpen((value) => !value)}
            >
              {isStyleEditorOpen ? 'Hide editor' : 'Edit style'}
            </Button>
          </Stack>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              p: 1.5,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'background.paper',
            }}
          >
            {EMAIL_TEMPLATE_STYLE_COLOR_FIELDS.map((field) => {
              const color = getStyleColor(field.name);

              return (
                <Box
                  key={field.name}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    minHeight: 32,
                    px: 1.25,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      border: 1,
                      borderColor: 'divider',
                      bgcolor: color,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', fontWeight: 600 }}
                  >
                    {field.summaryLabel}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: 'monospace', fontWeight: 700 }}
                  >
                    {color}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {isStyleEditorOpen && (
            <Box
              sx={{
                p: 2,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'background.paper',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(3, minmax(0, 1fr))',
                  },
                  columnGap: 2,
                  rowGap: 2,
                }}
              >
                {EMAIL_TEMPLATE_STYLE_COLOR_FIELDS.map((field) => (
                  <Controller
                    key={field.name}
                    name={field.name}
                    control={control}
                    render={({ field: colorField, fieldState }) => (
                      <Stack spacing={1}>
                        <Typography variant="subtitle2">
                          {field.editorLabel}
                        </Typography>
                        <Stack direction="row" spacing={1.5}>
                          <TextField
                            type="color"
                            size="small"
                            value={colorField.value}
                            onBlur={colorField.onBlur}
                            onChange={colorField.onChange}
                            inputProps={{
                              'aria-label': field.editorLabel,
                            }}
                            sx={{
                              width: 58,
                              flexShrink: 0,
                              '& input': {
                                height: 34,
                                p: 0.5,
                                cursor: 'pointer',
                              },
                            }}
                          />
                          <TextField
                            fullWidth
                            size="small"
                            value={colorField.value}
                            onBlur={colorField.onBlur}
                            onChange={colorField.onChange}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                          />
                        </Stack>
                      </Stack>
                    )}
                  />
                ))}
              </Box>

              <Stack spacing={1.5} sx={{ mt: 2.5 }}>
                <Controller
                  name="logo_enabled"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      label="Logo"
                      control={
                        <Switch
                          {...field}
                          checked={field.value}
                          onChange={(_event, checked) =>
                            field.onChange(checked)
                          }
                        />
                      }
                    />
                  )}
                />
                {currentStyle.logo_enabled && (
                  <Controller
                    name="logo_url"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        label="Logo URL"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                )}
              </Stack>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{ mt: 2.5 }}
              >
                <LoadingButton
                  variant="contained"
                  color="inherit"
                  loading={saveStyleMutation.isLoading}
                  disabled={isStyleMutationLoading}
                  onClick={handleSubmit((values) =>
                    saveStyleMutation.mutate(values)
                  )}
                >
                  Save style
                </LoadingButton>
                <LoadingButton
                  variant="outlined"
                  color="inherit"
                  loading={resetStyleMutation.isLoading}
                  disabled={isStyleMutationLoading}
                  onClick={() => resetStyleMutation.mutate()}
                >
                  Reset to default
                </LoadingButton>
              </Stack>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

export function EmailTemplateEditor({
  templateId,
  onSaved,
  previewStyle,
}: EmailTemplateEditorProps) {
  const queryClient = useQueryClient();
  const { errorMsg, successMsg } = useSnackbarMsg();
  const [previewPayload, setPreviewPayload] =
    useState<EmailTemplatePreviewPayload | null>(null);

  const methods = useForm<EmailTemplateFormValues>({
    resolver: yupResolver(EmailTemplateSchema),
    defaultValues,
  });

  const {
    control,
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const { data: template, isLoading } = useQuery({
    queryKey: ['emailTemplates', templateId],
    queryFn: () => getEmailTemplate(templateId),
    enabled: !!templateId,
    onSuccess: (data) => {
      reset(emailTemplateToFormValues(data));
    },
    onError: (error) =>
      errorMsg(error, 'Something went wrong while fetching email template'),
  });

  const watchedValues = useWatch({ control });
  const previewVariables = useMemo(
    () => template?.variables ?? null,
    [template]
  );

  const defaultHtmlContent = template ? template.custom_body_html ?? '' : '';

  useEffect(() => {
    if (!template) return undefined;

    const timeoutId = window.setTimeout(() => {
      const nextPreviewPayload: EmailTemplatePreviewPayload = {
        subject: watchedValues.subject ?? '',
        body_html: watchedValues.body_html ?? '',
        body_text: watchedValues.body_text ?? '',
        ...(previewStyle ?? {}),
      };

      if (previewVariables) {
        nextPreviewPayload.preview_variables = previewVariables;
      }

      setPreviewPayload(nextPreviewPayload);
    }, 400);

    return () => window.clearTimeout(timeoutId);
  }, [
    previewVariables,
    template,
    watchedValues.body_html,
    watchedValues.body_text,
    watchedValues.subject,
    previewStyle,
  ]);

  const {
    data: preview,
    isError: isPreviewError,
    isFetching: isPreviewFetching,
  } = useQuery({
    queryKey: ['emailTemplates', templateId, 'preview', previewPayload],
    queryFn: () =>
      previewEmailTemplate(
        templateId,
        previewPayload as EmailTemplatePreviewPayload
      ),
    enabled: !!template && !!previewPayload,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const previewSubject = preview?.subject ?? watchedValues.subject ?? '';
  const previewDocument =
    preview?.body_html ?? '<p>Email preview loading...</p>';

  const updateMutation = useMutation({
    mutationFn: (values: EmailTemplateFormValues) =>
      putEmailTemplate(emailTemplateFormToPayload(values), templateId),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['emailTemplates']);
      queryClient.setQueryData(['emailTemplates', templateId], data);
      successMsg('Email template updated successfully');
      onSaved?.();
    },
    onError: (error) =>
      errorMsg(error, 'Something went wrong while updating email template'),
  });

  const revertMutation = useMutation({
    mutationFn: () => {
      if (!template) {
        throw new Error('Email template has not loaded yet');
      }

      return putEmailTemplate(getEmailTemplateRevertPayload(), templateId);
    },
    onSuccess: (data) => {
      reset(emailTemplateToFormValues(data));
      queryClient.invalidateQueries(['emailTemplates']);
      queryClient.setQueryData(['emailTemplates', templateId], data);
      successMsg('Email template content reverted to defaults');
    },
    onError: (error) =>
      errorMsg(error, 'Something went wrong while reverting email template'),
  });

  const showSkeleton = isLoading || !template;
  const variables = template ? formatEmailTemplateVariables(template) : '';

  if (showSkeleton) {
    return <SkeletonLoadingTableData />;
  }

  return (
    <FormProvider
      methods={methods}
      onSubmit={handleSubmit((values) => updateMutation.mutate(values))}
    >
      <Stack spacing={3}>
        <Card>
          <CardContent>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip label={template.enabled ? 'Enabled' : 'Disabled'} />
                <Chip label={template.ref} variant="outlined" />
                {template.template_id && (
                  <Chip
                    label={`OneSignal: ${template.template_id}`}
                    variant="outlined"
                  />
                )}
              </Stack>
              <Typography variant="h6">{template.name}</Typography>
              {template.description && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {template.description}
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', md: 'center' }}
              >
                <Typography variant="h6">Editable content</Typography>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => revertMutation.mutate()}
                  disabled={revertMutation.isLoading}
                >
                  Reset to default
                </Button>
              </Stack>

              <RHFTextField name="subject" label="Subject" />
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={3}
                alignItems="flex-start"
              >
                <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                  <Box>
                    <Typography variant="subtitle1">HTML Body</Typography>
                  </Box>
                  <RHFTextField
                    name="body_html"
                    label="HTML content"
                    multiline
                    minRows={14}
                  />
                  <RHFTextField
                    name="body_text"
                    label="Plain-text body"
                    multiline
                    minRows={6}
                  />
                </Stack>

                <Stack
                  spacing={2}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    position: { md: 'sticky' },
                    top: { md: 24 },
                  }}
                >
                  <Typography variant="h6">Email preview</Typography>
                  <Box
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      bgcolor: 'background.neutral',
                    }}
                  >
                    <Typography variant="subtitle2">
                      Subject: {previewSubject}
                    </Typography>
                    {isPreviewFetching && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 0.5,
                          color: 'text.secondary',
                        }}
                      >
                        Updating preview...
                      </Typography>
                    )}
                    {isPreviewError && (
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', mt: 0.5, color: 'error.main' }}
                      >
                        Preview unavailable
                      </Typography>
                    )}
                  </Box>
                  <Box
                    key={previewDocument}
                    component="iframe"
                    title="Email preview"
                    sandbox=""
                    srcDoc={previewDocument}
                    sx={{
                      width: 1,
                      minHeight: 520,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: 'background.neutral',
                    }}
                  />
                </Stack>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6">Restore/default snapshot</Typography>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Default subject
                </Typography>
                <Box
                  sx={{
                    mt: 0.5,
                    p: 1.5,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.neutral',
                  }}
                >
                  <Typography variant="body2">
                    {template.custom_subject ?? 'No default subject snapshot'}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Default HTML content
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    mt: 0.5,
                    p: 1.5,
                    m: 0,
                    maxHeight: 320,
                    overflow: 'auto',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.neutral',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'monospace',
                    fontSize: 13,
                  }}
                >
                  {defaultHtmlContent || 'No default HTML snapshot'}
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Default plain-text body
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    mt: 0.5,
                    p: 1.5,
                    m: 0,
                    maxHeight: 240,
                    overflow: 'auto',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.neutral',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'monospace',
                    fontSize: 13,
                  }}
                >
                  {template.custom_body_text ??
                    'No default plain-text snapshot'}
                </Box>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6">Available variables</Typography>
              <TextField
                fullWidth
                size="small"
                value={variables || 'No variables configured'}
                multiline
                minRows={variables ? 6 : 2}
                InputProps={{ readOnly: true }}
              />
            </Stack>
          </CardContent>
        </Card>

        <Stack alignItems="flex-end" sx={{ pb: 2 }}>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSubmitting || updateMutation.isLoading}
          >
            Save Changes
          </LoadingButton>
        </Stack>
      </Stack>
    </FormProvider>
  );
}

export default function EmailTemplateForm() {
  const navigate = useNavigate();
  const { id } = useParams();

  if (!id) {
    return <Navigate to={settingsPath('templates')} replace />;
  }

  const templateId = Number(id);

  return (
    <Page title="Settings: Edit Email Template">
      <Container maxWidth={false}>
        <HeaderBreadcrumbs
          heading="Edit Email Template"
          links={[
            { name: 'Settings', href: ROOTS_SETTINGS },
            { name: 'Email Templates', href: settingsPath('templates') },
            { name: String(id) },
          ]}
        />

        <EmailTemplateEditor
          templateId={templateId}
          onSaved={() => navigate(settingsPath('templates'))}
        />
      </Container>
    </Page>
  );
}
